import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import {
  Activity, CheckCircle2, AlertTriangle, XCircle, HelpCircle,
  RefreshCw, TrendingUp, AlertCircle, Wallet,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Deliverability Dashboard
 * Shows a pre-blast traffic-light indicator so the admin can spot a carrier
 * block BEFORE wasting credits on a doomed blast.
 */
const DeliverabilityDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    try {
      const res = await fetch(`${API_URL}/api/automation/sms/deliverability`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (res.ok) setData(await res.json());
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
    // Refresh every 60 sec while page is open
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, []);

  if (loading) {
    return (
      <Card data-testid="deliverability-dashboard-loading">
        <CardContent className="py-8 text-center">
          <RefreshCw className="w-6 h-6 animate-spin mx-auto text-gray-400" />
        </CardContent>
      </Card>
    );
  }
  if (!data) return null;

  const health = data.health_status;
  const palette = {
    healthy: {
      bg: 'bg-green-50',
      border: 'border-green-300',
      ring: 'ring-green-200',
      text: 'text-green-800',
      icon: <CheckCircle2 className="w-7 h-7 text-green-700" />,
      label: 'Healthy',
      sub: 'Recent deliveries look great — safe to blast.',
    },
    degraded: {
      bg: 'bg-amber-50',
      border: 'border-amber-300',
      ring: 'ring-amber-200',
      text: 'text-amber-800',
      icon: <AlertTriangle className="w-7 h-7 text-amber-700" />,
      label: 'Degraded',
      sub: 'Some failures detected — verify with a test SMS first.',
    },
    blocked: {
      bg: 'bg-red-50',
      border: 'border-red-300',
      ring: 'ring-red-200',
      text: 'text-red-800',
      icon: <XCircle className="w-7 h-7 text-red-700" />,
      label: 'Blocked',
      sub: 'Most recent messages are failing. DO NOT blast.',
    },
    unknown: {
      bg: 'bg-gray-50',
      border: 'border-gray-300',
      ring: 'ring-gray-200',
      text: 'text-gray-700',
      icon: <HelpCircle className="w-7 h-7 text-gray-600" />,
      label: 'Unknown',
      sub: 'Not enough recent traffic — send a test SMS to verify.',
    },
  }[health] || {};

  const handleRefresh = () => { setRefreshing(true); load(); };

  return (
    <Card className={`border-2 ${palette.border}`} data-testid="deliverability-dashboard">
      <CardHeader className={palette.bg}>
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className={`p-3 rounded-xl bg-white/60 ring-4 ${palette.ring}`}>
              {palette.icon}
            </div>
            <div>
              <CardTitle className={`text-2xl font-black ${palette.text}`}>
                Deliverability: {palette.label}
              </CardTitle>
              <CardDescription className={`${palette.text} opacity-80`}>
                {palette.sub}
              </CardDescription>
              <p className={`text-sm mt-2 ${palette.text} font-medium`} data-testid="deliverability-guidance">
                {data.guidance}
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleRefresh}
            disabled={refreshing}
            className="gap-2"
            data-testid="refresh-deliverability-btn"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </Button>
        </div>
      </CardHeader>

      <CardContent className="pt-6 space-y-6">
        {/* Stats grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard
            icon={<TrendingUp className="w-4 h-4" />}
            label="Last 24h success"
            value={`${data.last_24h.success_rate}%`}
            sub={`${data.last_24h.delivered} ok / ${data.last_24h.failed} fail`}
            testid="stat-24h"
          />
          <StatCard
            icon={<Activity className="w-4 h-4" />}
            label="Last 7d success"
            value={`${data.last_7d.success_rate}%`}
            sub={`${data.last_7d.total} messages`}
            testid="stat-7d"
          />
          <StatCard
            icon={<AlertCircle className="w-4 h-4" />}
            label="Chronic failures"
            value={data.chronic_failures}
            sub="contacts ≥3 failures"
            testid="stat-chronic"
          />
          <StatCard
            icon={<Wallet className="w-4 h-4" />}
            label="SMS balance"
            value={`$${data.balance.credits.toFixed(2)}`}
            sub={data.balance.is_low ? 'LOW — top up' : 'available'}
            warn={data.balance.is_low}
            testid="stat-balance"
          />
        </div>

        {/* Recent blasts */}
        <div>
          <h4 className="font-bold text-sm text-gray-700 mb-2 uppercase tracking-wide">Recent blasts</h4>
          {data.recent_jobs.length === 0 ? (
            <p className="text-sm text-gray-500 py-3">No blasts yet.</p>
          ) : (
            <div className="space-y-2" data-testid="recent-blasts-list">
              {data.recent_jobs.map((j) => {
                const rate = j.success_rate;
                const barColor = rate >= 90 ? 'bg-green-500' : rate >= 50 ? 'bg-amber-500' : 'bg-red-500';
                return (
                  <div key={j.id} className="bg-gray-50 rounded-lg p-3 flex items-center gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-mono text-xs text-gray-500">{j.type}</span>
                        <span className="text-xs text-gray-400">·</span>
                        <span className="text-xs text-gray-500">
                          {j.started_at ? new Date(j.started_at).toLocaleString() : '—'}
                        </span>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div className={`h-full ${barColor}`} style={{ width: `${rate}%` }} />
                        </div>
                        <span className="text-sm font-bold text-gray-700 w-12 text-right">{rate}%</span>
                      </div>
                    </div>
                    <div className="text-right text-xs whitespace-nowrap">
                      <div className="text-green-700 font-medium">{j.sent} sent</div>
                      {j.failed > 0 && <div className="text-red-600">{j.failed} failed</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Error breakdown */}
        {data.error_breakdown.length > 0 && (
          <div>
            <h4 className="font-bold text-sm text-gray-700 mb-2 uppercase tracking-wide">
              Top error codes (last 7 days)
            </h4>
            <div className="space-y-1" data-testid="error-breakdown">
              {data.error_breakdown.map((e) => (
                <div key={e.code} className="flex items-center justify-between bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm">
                  <div>
                    <span className="font-mono font-bold text-red-700">{e.code}</span>
                    <span className="text-gray-700 ml-2">{e.label}</span>
                  </div>
                  <span className="text-red-700 font-bold">{e.count}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const StatCard = ({ icon, label, value, sub, warn, testid }) => (
  <div
    className={`rounded-lg p-3 border ${warn ? 'bg-amber-50 border-amber-300' : 'bg-white border-gray-200'}`}
    data-testid={testid}
  >
    <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
      {icon}
      <span>{label}</span>
    </div>
    <div className={`text-2xl font-black ${warn ? 'text-amber-700' : 'text-gray-900'}`}>{value}</div>
    <div className="text-xs text-gray-500 mt-0.5">{sub}</div>
  </div>
);

export default DeliverabilityDashboard;
